import deepmerge from 'deepmerge';
import {useRouter} from 'next/router';
import {useCallback, useEffect} from 'react';
import {useInView} from 'react-intersection-observer';
import {DeepPartial} from 'redux';
import {AppLinkFallbackType} from '../../../../../../applink/AppLinkFallbackType.enum';
import {pagewebUrlMatcher} from '../../../../../../applink/PagewebUrlMatcher.singleton';
import {SchemeLink} from '../../../../../../applink/SchemeLink';
import {DisplayAdContainer} from '../../../../../../common/containers/DisplayAdContainer/DisplayAdContainer';
import {getIsAgeRestricted} from '../../../../../../common/getIsAgeRestricted';
import {t, tt} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {stopPropagationEvent} from '../../../../../../common/shared/utils/stopPropagationEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Icon} from '../../../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {Text} from '../../../../../../design-system/atoms/Text/Text';
import {ScrollableHorizontalList} from '../../../../../../design-system/molecules/ScrollableHorizontalList/ScrollableHorizontalList';
import {AppDownload} from '../../../../../../design-system/organisms/AppDownload/AppDownload';
import {ContentTitleSection} from '../../../../../../design-system/organisms/ContentTitleSection/ContentTitleSection';
import {DisplayAdType} from '../../../../../../design-system/organisms/DisplayAd/DisplayAdType.enum';
import {PosterViewItem} from '../../../../../../design-system/organisms/PosterViewItem/PosterViewItem';
import {ViewerEndNextItem} from '../../../../../../design-system/organisms/ViewerEndNextItem/ViewerEndNextItem';
import {ViewerEndPreviewComment} from '../../../../../../design-system/organisms/ViewerEndPreviewComment/ViewerEndPreviewComment';
import {ViewerEndRelatedSeries} from '../../../../../../design-system/organisms/ViewerEndRelatedSeries/ViewerEndRelatedSeries';
import {ViewerEndSeriesInfo} from '../../../../../../design-system/organisms/ViewerEndSeriesInfo/ViewerEndSeriesInfo';
import {ViewerEndStarComment} from '../../../../../../design-system/organisms/ViewerEndStarComment/ViewerEndStarComment';
import {ViewerEndWaitfreeDescription} from '../../../../../../design-system/organisms/ViewerEndWaitfreeDescription/ViewerEndWaitfreeDescription';
import {ViewerGuideType} from '../../../../../../design-system/organisms/ViewerGuide/ViewerGuideType.enum';
import {commonErrorHandler} from '../../../../../../errors/commonErrorHandler';
import {
  ItemType,
  PosterViewItemFragment,
  SaleState,
  SlideType,
} from '../../../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../../../logging/aggregateAndSendLog';
import {
  LOG_CLICK_EVENT,
  LOG_SERIES_CLICK,
} from '../../../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../../../logging/putTiaraObjectProps';
import {TrackEventObject} from '../../../../../../logging/TrackEventObject.interface';
import {showToast} from '../../../../../../toast/toastHandler';
import {useLogin} from '../../../../../../user/useLogin.hook';
import {useSimpleSeriesSingleInfoQuery} from '../../../../../../wrapper';
import {
  useViewerEndQuery,
  useViewerInfoQuery,
} from '../../../../../../wrapper/viewerWrapper';
import {useSetSeriesMutationHandler} from '../../../../../inven/[invenType]/hook/useSetSeriesMutationHandler';
import {
  LOCAL_STORAGE_VIEWER_IS_PREVIEW_COMMENT_ON,
  VIEWER_TOAST_MESSAGES,
} from '../constant/viewerConstant';
import {useSeriesLikeHook} from '../hook/useSeriesLike';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';
import {setIsViewerPreviewCommentOn} from '../state/viewerInfoSlice';
import {updateCacheIsAlarmOn} from '../util/updateCacheIsAlarmOn';

export function ViewerEndContainer({
  seriesId,
  productId,
  onClickStar,
  className,
  ...props
}: {
  seriesId: number;
  productId: number;
  onClickStar: (parameters?: {fromSavedCommand?: boolean}) => void;
  className?: string;
}) {
  const dispatch = useTypedDispatch();
  const router = useRouter();
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const {isLogin, doLogin, user} = useLogin();
  const simpleSingleResult = useSimpleSeriesSingleInfoQuery({
    seriesId,
    productId,
  });
  const {data: simpleSingleData} = simpleSingleResult;
  const isSaleStateStop =
    simpleSingleData?.simpleSeriesSingleInfo?.seriesSaleState ===
      SaleState.Stop ||
    simpleSingleData?.simpleSeriesSingleInfo?.singleSaleState ===
      SaleState.Stop;

  const viewerEndData = useViewerEndQuery({seriesId, productId}).data;
  const relatedSeries = viewerEndData?.relatedSeries;
  const isLikeSeries = viewerEndData?.likeOn ?? false;
  const isAlarmOnSeries = viewerEndData?.alarmOn ?? false;
  const {isExposed, isPageViewer, isNonstopWatching} = useTypedSelector(
    state => {
      return {
        isExposed: state.viewerEnd.isExposed,
        isPageViewer:
          state.viewerInfo.currentViewerGuide === ViewerGuideType.Forward ||
          state.viewerInfo.currentViewerGuide === ViewerGuideType.Reverse,
        isNonstopWatching:
          !!state.viewerInfo.nonstopWatchingInSeries[seriesId]
            ?.isNonstopWatching,
      };
    }
  );

  const viewerInfoData = useViewerInfoQuery({seriesId, productId}).data;
  const viewerData = viewerInfoData;
  const isWaitFree = viewerInfoData?.seriesItem?.isWaitfree;
  const waitfreePeriodByMinute =
    viewerInfoData?.seriesItem?.waitfreePeriodByMinute;

  const {handleMoveNextProduct} = useViewerMenuHandler({
    isMobile,
  });
  const {onClickLikeSeries} = useSeriesLikeHook({seriesId, productId});

  const {onSetSeriesAlarmMutate} = useSetSeriesMutationHandler();
  const onClickAlarmOnSeries = useCallback(() => {
    if (!isLogin) {
      doLogin();
      return;
    }
    if (!seriesId || !productId) {
      return;
    }
    if (isWebView) {
      window?.KPApp?.onClickAlarm?.();
      return;
    }
    onSetSeriesAlarmMutate(seriesId, !isAlarmOnSeries)
      .then(result => {
        if (result.setSeriesAlarm.status !== 'Success') {
          return;
        }
        updateCacheIsAlarmOn({
          seriesId,
          productId,
          isAlarmOn: !isAlarmOnSeries,
        });
        const message = isAlarmOnSeries
          ? VIEWER_TOAST_MESSAGES.alarmOff
          : VIEWER_TOAST_MESSAGES.alarmOn;
        showToast({
          message: tt(message),
          isMobile: requestContext.requestInfo.display.isMobile,
        });
      })
      .catch(error => {
        commonErrorHandler(error);
      });
  }, [
    dispatch,
    doLogin,
    isAlarmOnSeries,
    isLogin,
    isWebView,
    productId,
    seriesId,
    onSetSeriesAlarmMutate,
  ]);

  const onClickContentHome = useCallback(() => {
    if (isWebView) {
      window?.KPApp?.onClickSeriesHome?.();
      return;
    }
    router.push(`/content/${seriesId}`);
  }, [isWebView, router, seriesId]);

  const onClickComment = useCallback(() => {
    if (isWebView) {
      window?.KPApp?.onClickMoreComment?.();
      return;
    }
    router.push(`/content/${seriesId}/viewer/${productId}/comment`);
  }, [isWebView, productId, router, seriesId]);

  const onClickPreviewComment = useCallback(() => {
    if (isWebView) {
      window?.KPApp?.onClickPreviewComment?.();
      return;
    }
    router.push(
      `/content/${seriesId}/viewer/${productId}/comment?sortType=${viewerEndData?.viewerEndPreviewComment?.sortOpt.param}`
    );
  }, [
    isWebView,
    productId,
    router,
    seriesId,
    viewerEndData?.viewerEndPreviewComment?.sortOpt,
  ]);

  const onClickNextItem = useCallback(() => {
    if (isWebView) {
      window?.KPApp?.onClickNextProduct?.();
      return;
    }
    if (!viewerEndData) {
      return;
    }
    handleMoveNextProduct();
  }, [handleMoveNextProduct, isWebView, viewerEndData]);

  const onClickShare = useCallback(() => {
    if (isWebView) {
      window?.KPApp?.onClickOsDefaultShare?.();
      return;
    }
  }, [isWebView]);

  const onClickRecommendItemOnWebView = useCallback(
    (index: number) => {
      if (isWebView) {
        window?.KPApp?.onClickRecommendItem?.(index);
        return;
      }
    },
    [isWebView]
  );

  const onClickGoToRelatedSeries = useCallback(
    (e: React.MouseEvent, buttonText: string) => {
      aggregateAndSendLog({
        targetElem: e.target as HTMLElement,
        actionType: 'Event',
        actionName: '연계작품_클릭',
        customProps: {
          user_action: 'click',
          is_last_episode: viewerEndData?.nextItem ? 'n' : 'y',
        },
        eventMeta: {
          id: String(relatedSeries?.seriesId),
          type: 'seriesId',
          name: relatedSeries?.title,
          category: relatedSeries?.category,
          subcategory: relatedSeries?.subcategory,
          series: viewerData?.seriesItem.title,
          series_id: String(seriesId),
          provider: 'service',
        },
        click: {
          layer1: '뷰어엔드',
          layer2: '연계작품',
          copy: buttonText,
          imp_id: 'contentlink',
          imp_provider: 'service',
        },
      });

      router.push(`/content/${relatedSeries?.seriesId}`);
    },
    [
      relatedSeries?.category,
      relatedSeries?.seriesId,
      relatedSeries?.subcategory,
      relatedSeries?.title,
      router,
      seriesId,
      viewerData?.seriesItem.title,
      viewerEndData?.nextItem,
    ]
  );

  const onInViewRelatedSeries = useCallback(() => {
    aggregateAndSendLog({
      actionType: 'ViewImp',
      actionName: '연계작품_노출',
      customProps: {
        user_action: 'imp',
        viewer_mode: isNonstopWatching ? '정주행모드' : '일반모드',
      },
      viewimp_contents: {
        imp_id: 'contentlink',
        imp_provider: 'service',
        imp_type: 'vimp',
        layer1: '뷰어엔드',
        layer2: '연계작품',
        id: String(relatedSeries?.seriesId),
        type: 'seriesId',
        name: relatedSeries?.title,
      },
    });
  }, [relatedSeries?.seriesId, relatedSeries?.title, isNonstopWatching]);

  const viewerEndResult = useViewerEndQuery({seriesId, productId}).data;
  const viewerPreviewComment =
    viewerEndResult?.viewerEndPreviewComment?.comment;

  const {isViewerPreviewCommentOn} = useTypedSelector(state => ({
    isViewerPreviewCommentOn: state.viewerInfo.isViewerPreviewCommentOn,
  }));
  const toggleIsViewerPreviewCommentOn = useCallback(() => {
    //뷰어 미리 보기 댓글 on/off를 토글
    dispatch(setIsViewerPreviewCommentOn(!isViewerPreviewCommentOn));
    window.localStorage.setItem(
      LOCAL_STORAGE_VIEWER_IS_PREVIEW_COMMENT_ON,
      String(!isViewerPreviewCommentOn)
    );
  }, [dispatch, isViewerPreviewCommentOn]);

  const {ref, inView, entry} = useInView();
  useEffect(() => {
    if (inView && entry?.target) {
      aggregateAndSendLog({
        targetElem: entry.target as HTMLElement,
        actionType: 'ViewImp',
        actionName: '뷰어엔드_노출',
        customProps: {
          user_action: 'imp',
        },
      });
    }
  }, [entry, inView]);

  const shouldShowRelatedSeries = !!relatedSeries;

  // 기다무와 연관작품이 둘다 설정되어 있으면 연관작품이 우선됩니다
  const shouldShowWaitfreeDescription =
    !shouldShowRelatedSeries &&
    !!(isWaitFree && waitfreePeriodByMinute && waitfreePeriodByMinute <= 180);

  if (!viewerEndData || !viewerData || !isExposed) {
    return null;
  }
  return (
    <div
      className={twMerge(
        isMobile ? 'bg-theme-bg-surface' : 'bg-theme-bg-base',
        className
      )}
      {...putTiaraObjectProps({
        click: {
          layer1: '뷰어엔드',
        },
        viewimp_contents: {
          imp_id: 'viewer_end',
          imp_provider: 'service',
          imp_type: 'vimp',
          layer1: '뷰어엔드',
        },
      })}
      {...props}
    >
      {shouldShowRelatedSeries && (
        <ViewerEndRelatedSeries
          relatedSeries={relatedSeries}
          isMobile={isMobile}
          onClickGoToRelatedSeries={onClickGoToRelatedSeries}
          onInViewRelatedSeries={onInViewRelatedSeries}
        />
      )}
      {
        // 기다무 고지 영역
        shouldShowWaitfreeDescription && (
          <ViewerEndWaitfreeDescription
            isMobile={isMobile}
            isPageViewer={isPageViewer}
            waitfreePeriodByMinute={waitfreePeriodByMinute}
          />
        )
      }
      {
        // 상단영역을 보여줄 필요가 없는 작품이라면 영상 뷰어가 아닌 모바일 뷰어는 DA 위에 56px의 마진을 가집니다.
        !shouldShowRelatedSeries &&
          !shouldShowWaitfreeDescription &&
          isMobile &&
          viewerData.item.slideType !== SlideType.TalkHumor && (
            <div className="h-56pxr w-full" />
          )
      }
      {isMobile && (
        <DisplayAdContainer
          type={DisplayAdType.Extended}
          displayAd={viewerEndData.displayAd}
          onClick={stopPropagationEvent}
          className="mx-11pxr mt-16pxr min-h-160pxr"
        />
      )}
      <div
        ref={ref}
        className={twMerge(
          'min-h-172pxr px-15pxr py-20pxr',
          isMobile
            ? 'bg-theme-bg-surface pb-12pxr pt-30pxr'
            : 'bg-theme-bg-surface-alt px-32pxr'
        )}
        {...putTiaraObjectProps({
          click: {
            imp_id: 'viewer_end',
            imp_provider: 'service',
          },
        })}
      >
        <ViewerEndSeriesInfo
          contentTitle={viewerData.seriesItem.title}
          onClickContentTitle={e =>
            LOG_CLICK_EVENT('콘텐츠홈_클릭', e, onClickContentHome)
          }
          authorName={viewerData.seriesItem.authors ?? ''}
          isLikeSeries={isLikeSeries}
          onClickLikeSeries={e =>
            LOG_CLICK_EVENT('좋아요_클릭', e, () => onClickLikeSeries())
          }
          isAlarmOnSeries={isAlarmOnSeries}
          onClickAlarmOnSeries={e =>
            LOG_CLICK_EVENT('알림_클릭', e, onClickAlarmOnSeries)
          }
          hasShareButton={isWebView}
          isTalkViewer={viewerData.item.slideType === SlideType.TalkNovel}
          onClickShareSeries={e =>
            LOG_CLICK_EVENT('회차공유_클릭', e, onClickShare)
          } // TODO : 공유 버튼에 대한 구현이 필요합니다.
          isSaleStateStop={isSaleStateStop}
          isMobile={isMobile}
        />

        <ViewerEndStarComment
          ratingCount={viewerEndData.ratingCount}
          ratingSum={viewerEndData.ratingSum}
          commentCount={viewerEndData.commentCount}
          isMobile={isMobile}
          onClickStar={e =>
            LOG_CLICK_EVENT('별점입력_클릭', e, () => onClickStar())
          }
          onClickComment={e =>
            LOG_CLICK_EVENT('회차댓글_클릭', e, onClickComment)
          }
          className={isMobile ? 'mt-20pxr' : 'mt-30pxr'}
        />

        {viewerPreviewComment && !!viewerPreviewComment.comment && (
          <ViewerEndPreviewComment
            {...putTiaraObjectProps({
              click: {
                copy: isViewerPreviewCommentOn ? 'on' : 'off',
              },
            })}
            userName={viewerPreviewComment.userName || ''}
            comment={viewerPreviewComment.comment || ''}
            isHidden={!isViewerPreviewCommentOn}
            onClickPreviewComment={e =>
              LOG_CLICK_EVENT('회차댓글미리보기_클릭', e, onClickPreviewComment)
            }
            onClickPreviewCommentOnOff={e =>
              LOG_CLICK_EVENT(
                '회차댓글미리보기설정_클릭',
                e,
                toggleIsViewerPreviewCommentOn
              )
            }
            className="mt-8pxr"
          />
        )}
      </div>
      <ViewerEndNextItem
        isVideoViewer={viewerData?.nextItem?.slideType === SlideType.TalkHumor}
        nextItem={viewerEndData.nextItem}
        onIssue={viewerEndData.onIssue}
        onClickNextItem={e => LOG_CLICK_EVENT('회차_클릭', e, onClickNextItem)}
        isSaleStateStop={isSaleStateStop}
        isMobile={isMobile}
        className={twMerge(
          isMobile
            ? 'mx-15pxr bg-theme-transparent-10'
            : 'mt-4pxr min-h-108pxr bg-theme-bg-surface-alt',
          'relative z-10'
        )}
        {...putTiaraObjectProps(
          viewerEndData.nextItem
            ? {
                eventMeta: {
                  id: String(viewerEndData.nextItem.productId),
                  type: 'productId',
                  name: viewerEndData.nextItem.title,
                  category: viewerData.seriesItem.category,
                  subcategory: viewerData.seriesItem.subcategory,
                  provider: '서비스',
                  series: viewerData.seriesItem.title,
                  series_id: String(seriesId),
                },
                click: {
                  imp_id: 'viewer_end',
                  imp_provider: 'service',
                },
              }
            : undefined
        )}
      />
      {isMobile && (
        <div
          className="my-6pxr flex h-38pxr items-center justify-center"
          onClick={e => LOG_CLICK_EVENT('콘텐츠홈_클릭', e, onClickContentHome)}
        >
          <Text className="font-small1 mr-2pxr text-theme-solid-100">
            {t('viewer_end_contenthome_button')}
          </Text>
          <Icon type={IconType.ButtonArrowRight} disableActiveEffect />
        </div>
      )}
      {viewerEndData.contentRecommendGroup?.items &&
        viewerEndData.contentRecommendGroup.items.length !== 0 && (
          <div
            className={
              isMobile
                ? 'mb-56pxr min-h-270pxr border-t-[3px] border-solid border-theme-bg-base-alt bg-theme-bg-surface'
                : 'relative mt-4pxr min-h-280pxr bg-theme-bg-surface-alt pt-16pxr'
            }
          >
            <ContentTitleSection
              sectionTitle={viewerEndData.contentRecommendGroup.title}
              isMobile={isMobile}
              description={viewerEndData.contentRecommendGroup.description}
              className="pt-15pxr"
            />

            <ScrollableHorizontalList
              isMobile={isMobile}
              gapPx={8}
              className={isMobile ? 'pb-20pxr' : 'static px-32pxr pb-14pxr'}
              innerLeftPx={isMobile ? 15 : 0}
              innerRightPx={isMobile ? 15 : 0}
              // 톡뷰어는 capture 이벤트로 이벤트를 제어해주고 있어서 요소에서 터치가 발생하면 window에서 캡처링 propagation을 막습니다.
              onTouchStartCapture={() => {
                if (viewerData.item.slideType !== SlideType.TalkNovel) return;
                window.addEventListener(
                  'touchmove',
                  stopPropagationEvent,
                  true
                );
              }}
              onTouchEndCapture={() => {
                if (viewerData.item.slideType !== SlideType.TalkNovel) return;
                window.removeEventListener(
                  'touchmove',
                  stopPropagationEvent,
                  true
                );
              }}
              {...putTiaraObjectProps({
                customProps: {
                  is_last_episode: viewerEndData.nextItem ? 'n' : 'y',
                },
                click: {layer2: '추천작품'},
              })}
              blockMouseEvent={viewerData.item.slideType === SlideType.Epub}
            >
              {viewerEndData.contentRecommendGroup.items.map((item, index) => {
                if (
                  item.type !== ItemType.PosterView ||
                  !viewerEndData.contentRecommendGroup
                ) {
                  return;
                }
                const recommendItem = item as PosterViewItemFragment;
                const eventLog = recommendItem.eventLog;
                const posterLogObj: DeepPartial<TrackEventObject> = {
                  click: {
                    ordnum: String(index + 1),
                    layer2: '추천작품',
                  },
                  ...(eventLog?.customProps?.helix_yn === 'y' && {
                    viewimp_contents: {
                      imp_ordnum: String(index + 1),
                      layer2: '추천작품',
                    },
                    customProps: {
                      helix_seed: String(seriesId),
                    },
                  }),
                };
                return (
                  <SchemeLink
                    key={recommendItem.id}
                    // 웹뷰일경우의 처리는 앱에서 합니다.
                    scheme={recommendItem.scheme}
                    preventEvent={isWebView}
                    onClick={e => {
                      stopPropagationEvent(e);
                      onClickRecommendItemOnWebView(index);
                    }}
                  >
                    <PosterViewItem
                      title={recommendItem.title || ''}
                      altText={recommendItem.altText || ''}
                      subtitleList={recommendItem.subtitleList}
                      posterSrc={recommendItem.thumbnail}
                      badgeList={recommendItem.badgeList || []}
                      statusBadge={recommendItem.statusBadge}
                      ageGradeBadge={recommendItem.ageGradeBadge}
                      discountRateText={recommendItem.discountRateText}
                      isAgeRestricted={getIsAgeRestricted({
                        age: user?.age,
                        authenticatedAt: user?.authenticatedAt,
                        ageGrade: recommendItem.ageGrade,
                        selfCensorship: recommendItem.selfCensorship,
                      })}
                      className="relative w-98pxr"
                      {...putTiaraObjectProps(
                        eventLog
                          ? deepmerge(eventLog, posterLogObj)
                          : posterLogObj
                      )}
                      onClickCapture={LOG_SERIES_CLICK}
                      onInView={
                        eventLog?.customProps?.helix_yn === 'y'
                          ? target => {
                              aggregateAndSendLog({
                                targetElem: target as HTMLElement,
                                actionType: 'ViewImp',
                                actionName: '작품_노출',
                                customProps: {
                                  user_action: 'imp',
                                },
                              });
                            }
                          : undefined
                      }
                    />
                  </SchemeLink>
                );
              })}
            </ScrollableHorizontalList>
          </div>
        )}
      {isMobile && !isWebView && (
        <div className="relative z-10">
          <AppDownload
            isMobile={isMobile}
            isViewerEnd={true}
            onClickAppLink={() =>
              window.location.assign(
                pagewebUrlMatcher.getCurrentAppLink({
                  schemePrefixType: 'applink',
                  fallbackType: AppLinkFallbackType.Store,
                })
              )
            }
          />
        </div>
      )}
    </div>
  );
}
